import { useUser } from "@shared/utils/hooks/misc";
import { RocketIcon } from "common/Icons/Icons";
import styled, { css, useTheme } from "styled-components";
import { Sparkles } from "lucide-react";
import Text from "common/Typography/Text";
import { CSSProperties } from "react";
import { getPurchaseSubscriptionPath } from "features/settings/common/utils";
import { useRestrictedCountriesPurchase } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { IS_WHOP } from "common/constants.ts";
import { useHideBillingButtons } from "features/settings/common/hooks.tsx";

interface GetFullAccessButtonProps {
  style?: CSSProperties;
}

const UpgradeButton = ({ style }: GetFullAccessButtonProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { user } = useUser();
  const { onPurchaseClick } = useRestrictedCountriesPurchase();
  const isDarkMode = theme.isDarkMode;
  const hideUpgradeButton = useHideBillingButtons();

  if (
    user?.isMaxSubscription() ||
    user?.is_sub_user ||
    IS_WHOP ||
    hideUpgradeButton
  )
    return null;

  return (
    <StyledButton
      $isDarkMode={isDarkMode}
      style={style}
      onClick={() =>
        onPurchaseClick(getPurchaseSubscriptionPath({ isAllPlans: true }))
      }
    >
      {theme.isNewLook ? (
        <Sparkles size={15} strokeWidth={1.75} className="upgrade-icon" />
      ) : (
        <StyledRocketIcon />
      )}
      <ButtonText>{t("user_info.upgrade_button")}</ButtonText>
    </StyledButton>
  );
};

export default UpgradeButton;

const StyledButton = styled.div<{ $isDarkMode?: boolean }>`
  background: ${(p) =>
    p.$isDarkMode
      ? "linear-gradient(92.77deg, #6ADAFB -2.78%, #70A8FF 104.85%)"
      : "linear-gradient(117.02deg, #6abbfa 4.73%, #0f54e9 98.99%)"};
  white-space: nowrap;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 6px 16px;
  cursor: pointer;
  width: fit-content;
  &:hover {
    opacity: 0.85;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A quiet pill next to Create Bot; the icon carries the accent. */
      height: 36px;
      padding: 0 14px;
      border-radius: 999px;
      background: ${p.theme.colors.cardBgColor};
      border: ${p.theme.borders.card};
      transition: border-color 150ms, background-color 150ms;
      .upgrade-icon {
        color: ${p.theme.colors.purple};
      }
      &:hover {
        opacity: 1;
        border-color: ${p.theme.colors.midBlue};
        background: ${p.theme.colors.cardLightBgColor};
      }
    `}
`;
const ButtonText = styled(Text)`
  color: ${(p) => p.theme.colors.buttonText};
  margin-left: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-left: 6px;
      color: ${p.theme.colors.titleColor};
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
    `}
`;
const StyledRocketIcon = styled(RocketIcon)`
  width: 16px;
  height: 16px;
  fill: ${(p) => p.theme.colors.buttonText};
`;
